import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import MarketOverviewCard from './components/MarketOverviewCard';
import AlertPanel from './components/AlertPanel';
import PriceChart from './components/PriceChart';
import QuickActions from './components/QuickActions';
import PerformanceMetrics from './components/PerformanceMetrics';
import MarketFilters from './components/MarketFilters';
import CorrelationHeatmap from './components/CorrelationHeatmap';

const MainDashboard = () => {
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1d');
  const [selectedDataSource, setSelectedDataSource] = useState('all');
  const [chartTimeframe, setChartTimeframe] = useState('1D');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock real-time data
  const [marketData, setMarketData] = useState({
    eua: {
      price: 85.42,
      change: 2.15,
      changePercent: 2.58,
      volume: '2.4M',
      isPositive: true
    },
    oil: {
      price: 78.96,
      change: -1.23,
      changePercent: -1.53,
      volume: '145K',
      isPositive: false
    }
  });

  // Mock alerts data
  const alerts = [
    {
      id: 1,
      type: 'risk',
      priority: 'high',
      title: 'High Volatility Alert',
      message: 'EUA prices showing unusual volatility patterns. Consider risk management.',
      timestamp: '2 min ago',
      action: 'View Details'
    },
    {
      id: 2,
      type: 'compliance',
      priority: 'medium',
      title: 'Regulatory Deadline',
      message: 'EU ETS Phase 4 compliance report due in 5 days.',
      timestamp: '15 min ago',
      action: 'Open Checklist'
    },
    {
      id: 3,
      type: 'news',
      priority: 'medium',
      title: 'Market Impact News',
      message: 'ECB policy announcement may affect carbon credit demand.',
      timestamp: '1 hour ago',
      action: 'Read More'
    },
    {
      id: 4,
      type: 'price',
      priority: 'low',
      title: 'Price Target Reached',
      message: 'Oil futures reached your target price of $79.00.',
      timestamp: '2 hours ago',
      action: 'View Position'
    }
  ];

  // Mock chart data
  const euaChartData = [
    { time: '09:00', price: 83.25 },
    { time: '10:00', price: 84.10 },
    { time: '11:00', price: 83.85 },
    { time: '12:00', price: 85.20 },
    { time: '13:00', price: 84.95 },
    { time: '14:00', price: 85.42 },
    { time: '15:00', price: 85.15 },
    { time: '16:00', price: 85.42 }
  ];

  const oilChartData = [
    { time: '09:00', price: 80.15 },
    { time: '10:00', price: 79.85 },
    { time: '11:00', price: 79.45 },
    { time: '12:00', price: 78.90 },
    { time: '13:00', price: 79.20 },
    { time: '14:00', price: 78.75 },
    { time: '15:00', price: 78.96 },
    { time: '16:00', price: 78.96 }
  ];

  // Mock performance metrics
  const performanceMetrics = [
    {
      id: 1,
      type: 'pnl',
      label: 'Daily P&L',
      value: '+12,450',
      unit: 'EUR',
      change: 8.5
    },
    {
      id: 2,
      type: 'volume',
      label: 'Volume Traded',
      value: '2.8M',
      unit: 'lots',
      change: 15.2
    },
    {
      id: 3,
      type: 'volatility',
      label: 'Avg Volatility',
      value: '18.5',
      unit: '%',
      change: -2.1
    },
    {
      id: 4,
      type: 'correlation',
      label: 'EUA-Oil Corr',
      value: '0.72',
      unit: '',
      change: 5.8
    }
  ];

  // Mock correlation matrix data
  const correlationData = [
    [1.00, 0.72, 0.45, 0.38, 0.62],
    [0.72, 1.00, 0.85, 0.67, 0.54],
    [0.45, 0.85, 1.00, 0.78, 0.43],
    [0.38, 0.67, 0.78, 1.00, 0.56],
    [0.62, 0.54, 0.43, 0.56, 1.00]
  ];

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketData(prev => ({
        eua: {
          ...prev?.eua,
          price: prev?.eua?.price + (Math.random() - 0.5) * 0.5,
          change: prev?.eua?.change + (Math.random() - 0.5) * 0.1
        },
        oil: {
          ...prev?.oil,
          price: prev?.oil?.price + (Math.random() - 0.5) * 0.3,
          change: prev?.oil?.change + (Math.random() - 0.5) * 0.1
        }
      }));
      setLastUpdate(new Date());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setLastUpdate(new Date());
    // Simulate data refresh
    console.log('Refreshing dashboard data...');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Main Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  Real-time market intelligence for EUA & Oil Futures trading
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span>Live data • Updated {lastUpdate?.toLocaleTimeString()}</span>
              </div>
            </div>
          </div>

          {/* Market Filters */}
          <div className="mb-6">
            <MarketFilters
              selectedMarket={selectedMarket}
              onMarketChange={setSelectedMarket}
              selectedTimeframe={selectedTimeframe}
              onTimeframeChange={setSelectedTimeframe}
              selectedDataSource={selectedDataSource}
              onDataSourceChange={setSelectedDataSource}
              onRefresh={handleRefresh}
            />
          </div>

          {/* Market Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <MarketOverviewCard
              market="EUA"
              price={`€${marketData?.eua?.price?.toFixed(2)}`}
              change={marketData?.eua?.change >= 0 ? `+${marketData?.eua?.change?.toFixed(2)}` : marketData?.eua?.change?.toFixed(2)}
              changePercent={marketData?.eua?.changePercent?.toFixed(2)}
              volume={marketData?.eua?.volume}
              isPositive={marketData?.eua?.isPositive}
            />
            <MarketOverviewCard
              market="Oil"
              price={`$${marketData?.oil?.price?.toFixed(2)}`}
              change={marketData?.oil?.change >= 0 ? `+${marketData?.oil?.change?.toFixed(2)}` : marketData?.oil?.change?.toFixed(2)}
              changePercent={marketData?.oil?.changePercent?.toFixed(2)}
              volume={marketData?.oil?.volume}
              isPositive={marketData?.oil?.isPositive}
            />
          </div>

          {/* Charts and Alerts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 space-y-6">
              <PriceChart
                title="EUA Price Movement"
                data={euaChartData}
                color="var(--color-success)"
                timeframe={chartTimeframe}
                onTimeframeChange={setChartTimeframe}
              />
              <PriceChart
                title="Oil Futures Price Movement"
                data={oilChartData}
                color="var(--color-warning)"
                timeframe={chartTimeframe}
                onTimeframeChange={setChartTimeframe}
              />
            </div>
            <div className="space-y-6">
              <AlertPanel alerts={alerts} />
              <QuickActions />
            </div>
          </div>

          {/* Performance and Correlation Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformanceMetrics metrics={performanceMetrics} />
            <CorrelationHeatmap correlationData={correlationData} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainDashboard;